import { produce } from 'immer';
import { tryParseJsonText } from 'app/lib/utils';
import { extractError } from 'app/lib/error';
import { ErrorLevel } from 'app/lib/constants';
import {
    TOGGLE_CREATE_JOB_DIALOG,
    LIST_MODELS_ACTION_TYPE,
    VALIDATE_JOBS_ACTION_TYPE,
    LIST_TAGS_ACTION_TYPE,
    GET_OUTPUT_COLUMNS_ACTION_TYPE,
    TOGGLE_DEPLOY_MODEL_DIALOG,
    DEPLOY_MODEL_ACTION_TYPE,
    REDEPLOY_MODEL_ACTION_TYPE,
    LIST_ENVIRONMENT_DATABASES,
    LIST_ENVIRONMENT_SCHEMAS,
    LIST_ENVIRONMENT_ROLES,
    LIST_ENVIRONMENT_WAREHOUSES,
    TOGGLE_CONVERT_TO_MODEL_DIALOG,
    CONVERT_TO_MODEL_ACTION_TYPE,
    QUEUE_DEPLOYMENTS_FOR_CONVERSION,
    OPEN_CONVERT_ALL_DIALOG,
    CLOSE_CONVERT_ALL_DIALOG,
    GET_LAST_MODEL_JOB_RUN,
    LIST_DATABASES,
    LIST_SCHEMAS
} from './types.js';
import { ConversionStatus } from './constants.js';
import { parseCheckResults, getJobRunStatuses } from './utils.js';

export const INITIAL_STATE = {
    isCreateJobDialogOpen: false,
    deployModelDialog: {
        isOpen: false,
        entityId: null
    },
    lastCreatedTag: null,
    tags: {
        data: [],
        isLoading: false
    },
    models: {
        data: [],
        isLoading: false
    },
    validatedJobs: {
        data: [],
        isLoading: false
    },
    outputColumns: {
        isLoading: false,
        byTagId: {}
    },
    deployModel: {
        isRunning: false,
        error: null
    },
    environmentDatabases: {
        isLoading: false,
        data: [],
        error: null
    },
    environmentSchemas: {
        isLoading: false,
        data: [],
        error: null
    },
    environmentRoles: {
        isLoading: false,
        data: [],
        error: null
    },
    environmentWarehouses: {
        isLoading: false,
        data: [],
        error: null
    },
    convertToModelDialog: {
        isOpen: false,
        entityId: null
    },
    conversion: {
        isOpen: false,
        deployments: {},
        statusById: {}
    },
    jobLastRunStatuses: {}
};

export const projectModelsReducer = produce((draft, action) => {
    switch (action.type) {
        case TOGGLE_CREATE_JOB_DIALOG:
            draft.isCreateJobDialogOpen = !draft.isCreateJobDialogOpen;
            draft.lastCreatedTag = action.meta?.tag;
            if (!draft.isCreateJobDialogOpen) {
                draft.environmentDatabases = INITIAL_STATE.environmentDatabases;
                draft.environmentSchemas = INITIAL_STATE.environmentSchemas;
            }
            break;
        case TOGGLE_DEPLOY_MODEL_DIALOG:
            draft.deployModelDialog.isOpen = !draft.deployModelDialog.isOpen;
            draft.deployModelDialog.entityId = action.meta.entityId;
            draft.deployModel.error = null;
            if (!draft.deployModelDialog.isOpen) {
                draft.environmentDatabases = INITIAL_STATE.environmentDatabases;
                draft.environmentSchemas = INITIAL_STATE.environmentSchemas;
            }
            break;
        case LIST_TAGS_ACTION_TYPE.STARTED:
            draft.tags.data = [];
            draft.tags.isLoading = true;
            break;
        case LIST_TAGS_ACTION_TYPE.SUCCEEDED:
            draft.tags.data = action.payload.entries;
            break;
        case LIST_TAGS_ACTION_TYPE.ENDED:
            draft.tags.isLoading = false;
            break;
        case LIST_MODELS_ACTION_TYPE.STARTED:
            draft.models.isLoading = true;
            break;
        case LIST_MODELS_ACTION_TYPE.SUCCEEDED:
            draft.models.data = action.payload.entries.map((entry) => ({
                ...entry,
                uid: `_v${entry.id}`
            }));
            break;
        case LIST_MODELS_ACTION_TYPE.ENDED:
            draft.models.isLoading = false;
            break;
        case VALIDATE_JOBS_ACTION_TYPE.STARTED:
            draft.validatedJobs.isLoading = true;
            break;
        case VALIDATE_JOBS_ACTION_TYPE.SUCCEEDED:
            draft.validatedJobs.data = action.payload.entries;
            break;
        case VALIDATE_JOBS_ACTION_TYPE.ENDED:
            draft.validatedJobs.isLoading = false;
            break;
        case GET_OUTPUT_COLUMNS_ACTION_TYPE.STARTED:
            draft.outputColumns.isLoading = true;
            draft.outputColumns.byTagId[action.payload.tagId] = [];
            break;
        case GET_OUTPUT_COLUMNS_ACTION_TYPE.SUCCEEDED:
            draft.outputColumns.byTagId[action.params.tagId] = action.payload.entries;
            break;
        case GET_OUTPUT_COLUMNS_ACTION_TYPE.ENDED:
            draft.outputColumns.isLoading = false;
            break;
        case REDEPLOY_MODEL_ACTION_TYPE.STARTED:
        case DEPLOY_MODEL_ACTION_TYPE.STARTED:
            draft.deployModel.isRunning = true;
            draft.deployModel.error = null;
            break;
        case REDEPLOY_MODEL_ACTION_TYPE.SUCCEEDED:
        case DEPLOY_MODEL_ACTION_TYPE.SUCCEEDED:
            draft.deployModelDialog.isOpen = false;
            break;
        case DEPLOY_MODEL_ACTION_TYPE.REJECTED:
        case DEPLOY_MODEL_ACTION_TYPE.FAILED:
        case REDEPLOY_MODEL_ACTION_TYPE.REJECTED:
        case REDEPLOY_MODEL_ACTION_TYPE.FAILED:
            draft.deployModel.error = extractError(action.payload);
            break;
        case REDEPLOY_MODEL_ACTION_TYPE.ENDED:
        case DEPLOY_MODEL_ACTION_TYPE.ENDED:
            draft.deployModel.isRunning = false;
            break;
        case LIST_DATABASES.STARTED:
        case LIST_ENVIRONMENT_DATABASES.STARTED:
            draft.environmentDatabases.isLoading = true;
            draft.environmentDatabases.error = null;
            break;
        case LIST_ENVIRONMENT_DATABASES.SUCCEEDED:
            draft.environmentDatabases.data = action.payload.value?.map(({ name }) => ({
                name,
                value: name
            }));
            break;
        case LIST_DATABASES.FAILED:
        case LIST_ENVIRONMENT_DATABASES.FAILED:
            draft.environmentDatabases.error = extractError(action.payload);
            break;
        case LIST_DATABASES.SUCCEEDED: {
            const { valuesByColumn } = action.payload;

            draft.environmentDatabases.data = valuesByColumn[1].map((database) => ({
                name: database,
                value: database
            }));
            break;
        }
        case LIST_DATABASES.ENDED:
        case LIST_ENVIRONMENT_DATABASES.ENDED:
            draft.environmentDatabases.isLoading = false;
            break;
        case LIST_SCHEMAS.STARTED:
        case LIST_ENVIRONMENT_SCHEMAS.STARTED:
            draft.environmentSchemas.isLoading = true;
            draft.environmentSchemas.error = null;
            break;
        case LIST_ENVIRONMENT_SCHEMAS.SUCCEEDED:
            draft.environmentSchemas.data =
                action.payload.value?.map(({ name, database_name }) => ({
                    name,
                    value: name,
                    database: database_name
                })) ?? [];
            break;
        case LIST_SCHEMAS.FAILED:
        case LIST_ENVIRONMENT_SCHEMAS.FAILED:
            draft.environmentSchemas.error = extractError(action.payload);
            break;
        case LIST_SCHEMAS.ENDED:
        case LIST_ENVIRONMENT_SCHEMAS.ENDED:
            draft.environmentSchemas.isLoading = false;
            break;
        case LIST_SCHEMAS.SUCCEEDED: {
            const { valuesByColumn } = action.payload;
            draft.environmentSchemas.data = valuesByColumn[1].map((schema, index) => ({
                name: schema,
                value: schema,
                database: valuesByColumn[3][index]
            }));
            break;
        }
        case LIST_ENVIRONMENT_ROLES.STARTED:
            draft.environmentRoles.isLoading = true;
            draft.environmentRoles.error = null;
            draft.environmentRoles.data = [];
            break;
        case LIST_ENVIRONMENT_ROLES.SUCCEEDED: {
            const roles = tryParseJsonText(action.payload?.value[0]?.ROLES);
            draft.environmentRoles.data = roles?.map((role) => ({
                name: role
            }));
            break;
        }
        case LIST_ENVIRONMENT_ROLES.FAILED:
            draft.environmentRoles.error = extractError(action.payload);
            break;
        case LIST_ENVIRONMENT_ROLES.ENDED:
            draft.environmentRoles.isLoading = false;
            break;
        case LIST_ENVIRONMENT_WAREHOUSES.STARTED:
            draft.environmentWarehouses.isLoading = true;
            draft.environmentWarehouses.error = null;
            draft.environmentWarehouses.data = [];
            break;
        case LIST_ENVIRONMENT_WAREHOUSES.SUCCEEDED:
            draft.environmentWarehouses.data = action.payload.value?.map(({ name }) => ({
                name
            }));
            break;
        case LIST_ENVIRONMENT_WAREHOUSES.FAILED:
            draft.environmentWarehouses.error = extractError(action.payload);
            break;
        case LIST_ENVIRONMENT_WAREHOUSES.ENDED:
            draft.environmentWarehouses.isLoading = false;
            break;
        case TOGGLE_CONVERT_TO_MODEL_DIALOG:
            draft.convertToModelDialog.isOpen = !draft.convertToModelDialog.isOpen;
            draft.convertToModelDialog.entityId = action.meta.entityId;
            break;
        case OPEN_CONVERT_ALL_DIALOG: {
            draft.conversion.deployments = action.payload.deployments;
            draft.conversion.statusById = {};
            draft.conversion.isOpen = true;
            break;
        }
        case CLOSE_CONVERT_ALL_DIALOG: {
            draft.conversion.isOpen = false;
            draft.conversion.statusById = {};
            draft.conversion.deployments = [];
            break;
        }
        case QUEUE_DEPLOYMENTS_FOR_CONVERSION: {
            draft.conversion.statusById = {};
            for (const id of action.payload.entityIds) {
                draft.conversion.statusById[id] = ConversionStatus.PENDING;
            }
            break;
        }
        case CONVERT_TO_MODEL_ACTION_TYPE.STARTED:
            draft.conversion.statusById[action.payload.nodeUuid] = ConversionStatus.STARTED;
            break;
        case CONVERT_TO_MODEL_ACTION_TYPE.SUCCEEDED:
            draft.conversion.statusById[action.params.nodeUuid] = ConversionStatus.SUCCEEDED;
            break;
        case CONVERT_TO_MODEL_ACTION_TYPE.FAILED:
            draft.conversion.statusById[action.params.nodeUuid] = ConversionStatus.FAILED;
            break;
        case GET_LAST_MODEL_JOB_RUN.SUCCEEDED: {
            const { entityId } = action.meta;
            const checkResults = action.payload?.executionMetadata?.POST_CHECK_RESULTS ?? [];
            const failedChecks = checkResults.filter(({ passed }) => !passed);
            const jobRunErrors = getJobRunStatuses(action.payload);

            draft.jobLastRunStatuses[entityId] = [
                ...parseCheckResults(failedChecks, ErrorLevel.ERROR),
                ...jobRunErrors
            ];
            break;
        }
        default:
            break;
    }
}, INITIAL_STATE);
